import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider
} from '@mui/material';
import { collection, query, where, getDocs, orderBy, getDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';

const Permanences = () => {
  const { userData, currentUser } = useAuth();
  const [permanences, setPermanences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPermanences = async () => {
      try {
        setLoading(true);
        if (!currentUser?.uid) return;

        // Récupérer les permanences visibles pour le personnel
        const permanencesRef = collection(db, 'permanences');
        
        // Récupérer toutes les permanences visibles
        const q = query(
          permanencesRef,
          where('visibleToPersonnel', '==', true),
          where('establishmentId', '==', userData.establishmentId)
        );
        
        const querySnapshot = await getDocs(q);
        
        // Filtrer côté client pour ne garder que les permanences où l'utilisateur est assigné
        const filteredDocs = querySnapshot.docs.filter(doc => {
          const data = doc.data();
          return data.personnel && data.personnel.includes(currentUser.uid);
        });
        
        // 2. Trier les résultats filtrés côté client
        const sortedResults = filteredDocs.sort((a, b) => {
          const dateA = a.data().createdAt?.toDate() || new Date(0);
          const dateB = b.data().createdAt?.toDate() || new Date(0);
          return dateB - dateA; // tri décroissant (plus récent d'abord)
        });
        const permanencesList = [];
        
        for (const docSnapshot of sortedResults) {
          const permanence = docSnapshot.data();
          
          // Récupérer les informations de la période
          const periodRef = doc(db, 'establishments', userData.establishmentId, 'vacances', permanence.period);
          const periodDoc = await getDoc(periodRef);
          const periodData = periodDoc.exists() ? periodDoc.data() : null;
          
          permanencesList.push({
            id: docSnapshot.id,
            ...permanence,
            periodName: periodData?.nom || 'Période inconnue',
          });
        }
        
        setPermanences(permanencesList);
      } catch (err) {
        console.error('Erreur lors du chargement des permanences:', err);
        setError('Erreur lors du chargement des permanences');
      } finally {
        setLoading(false);
      }
    };

    fetchPermanences();
  }, [currentUser, userData.establishmentId]);

  // Fonction pour formater les dates
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd MMMM yyyy', { locale: fr });
    } catch (error) {
      return dateString;
    }
  };

  // Grouper les permanences par période
  const permanencesByPeriod = permanences.reduce((acc, permanence) => {
    if (!acc[permanence.period]) {
      acc[permanence.period] = {
        periodName: permanence.periodName,
        items: []
      };
    }
    acc[permanence.period].items.push(permanence);
    return acc;
  }, {});

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom component="h1">
        Mes Permanences
      </Typography>
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>
      ) : permanences.length === 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>Aucune permanence n'est disponible pour le moment.</Alert>
      ) : (
        <Box sx={{ mt: 3 }}>
          {Object.entries(permanencesByPeriod).map(([periodId, { periodName, items }]) => (
            <Card key={periodId} sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {periodName}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Grid container spacing={2}>
                  {items.map((permanence) => (
                    <Grid item xs={12} md={6} key={permanence.id}>
                      <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CalendarIcon color="primary" sx={{ mr: 1 }} />
                          <Typography variant="subtitle1">
                            Permanence {permanence.isChefService ? '(Chef de service)' : ''}
                          </Typography>
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Horaires: {permanence.startTime} - {permanence.endTime}
                        </Typography>
                        
                        <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                          Dates:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                          {[...permanence.days].sort((a, b) => {
                            // Trier les dates dans l'ordre chronologique
                            const dateA = new Date(a);
                            const dateB = new Date(b);
                            return dateA - dateB;
                          }).map((day, index) => (
                            <Chip 
                              key={index} 
                              label={formatDate(day)} 
                              size="small" 
                              color="primary" 
                              variant="outlined" 
                            />
                          ))}
                        </Box>
                        
                        {permanence.observations && (
                          <>
                            <Typography variant="body2" sx={{ fontWeight: 'bold', mt: 1 }}>
                              Observations:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {permanence.observations}
                            </Typography>
                          </>
                        )}
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}
    </Container>
  );
};

export default Permanences;
